/**
 * Jeu de démo local — aucune écriture API / BDD.
 * Sert à montrer le tableau de bord au client.
 */
import { inclusiveDays } from "./period";
import type { DashActivityItem, DashBilling, DashKpi, DashMailDay, DashQueueItem, DashRealized } from "./types";

export type DashDemoPayload = {
  kpis: DashKpi[];
  overdue: DashQueueItem[];
  upcoming: DashQueueItem[];
  quotes: DashQueueItem[];
  recurring: DashQueueItem[];
  activity: DashActivityItem[];
  mail_days: DashMailDay[];
  billing: DashBilling;
  realized: DashRealized;
};

export const DASH_DEMO: DashDemoPayload = {
  kpis: [
    { id: "overdue", label: "En retard", hint: "Factures validées, échéance dépassée", value: "2 480 €", tone: "warn", bar: 62 },
    { id: "upcoming", label: "À encaisser", hint: "Factures envoyées, pas encore échues", value: "4 120 €", bar: 48 },
    { id: "paid_month", label: "Encaissé ce mois", hint: "Somme des paiements", value: "8 640 €", bar: 76 },
    { id: "quotes", label: "Devis à relancer", hint: "Expirés ou bientôt expirés", value: "2", bar: 40 },
  ],
  overdue: [
    { id: "o1", title: "F2026-0012 · Atelier Nord", meta: "Échue le 2 sept. · 1 440 €" },
    { id: "o2", title: "F2026-0009 · Maison Lumen", meta: "Échue le 28 août · 1 040 €" },
  ],
  upcoming: [
    { id: "u1", title: "F2026-0018 · Studio Seth", meta: "Le 18 sept. · 1 920 €" },
    { id: "u2", title: "F2026-0019 · Café des Halles", meta: "Le 21 sept. · 1 200 €" },
    { id: "u3", title: "F2026-0020 · Atelier Nord", meta: "Le 26 sept. · 1 000 €" },
  ],
  quotes: [
    { id: "q1", title: "D2026-0007 · Maison Lumen", meta: "Validité au 10 sept. · 3 600 €" },
    { id: "q2", title: "D2026-0008 · Brasserie Octave", meta: "Validité au 12 sept. · 890 €" },
  ],
  recurring: [
    { id: "r1", title: "Maintenance · Studio Seth", meta: "Prochain envoi le 1er oct. · mensuel" },
    { id: "r2", title: "Hébergement · Café des Halles", meta: "Prochain envoi le 5 oct. · mensuel" },
  ],
  billing: {
    months: [
      { key: "prev", label: "Août 2026", amount: "6 240,00 €", detail: "8 envoyées · 5 200,00 € HT" },
      { key: "current", label: "Septembre 2026", amount: "409,26 €", detail: "2 envoyées · 341,08 € HT" },
      { key: "next", label: "Octobre 2026", amount: "10 942,24 €", detail: "80 à envoyer · 9 118,45 € HT" },
    ],
    upcoming_title: "À envoyer en octobre",
    days: [
      { day: "2026-10-01", label: "1er octobre", count_label: "33 factures", amount: "7 886,22 €", detail: "6 571,84 € HT" },
      { day: "2026-10-05", label: "5 octobre", count_label: "47 factures", amount: "3 056,02 €", detail: "2 546,61 € HT" },
    ],
  },
  realized: {
    month_label: "Octobre 2026",
    month_range: "1er octobre 2026",
    month_imported: false,
    month_prev_label: "Septembre 2026",
    month_prev_range: "1er septembre 2026",
    month_prev_imported: false,
    month_ly_label: "Octobre 2025",
    month_ly_range: "1er octobre 2025",
    month_ly_imported: false,
    year_label: "2026",
    year_range: "1er janvier – 1er octobre 2026",
    year_imported: false,
    year_prev_label: "2025",
    year_prev_range: "1er janvier – 1er octobre 2025",
    year_prev_imported: false,
    month_ca_ht_cents: 657184,
    month_vat_cents: 131437,
    month_prev_ca_ht_cents: 49468,
    month_prev_vat_cents: 9894,
    month_ly_ca_ht_cents: 120000,
    month_ly_vat_cents: 24000,
    year_ca_ht_cents: 4210000,
    year_vat_cents: 842000,
    year_prev_ca_ht_cents: 3800000,
    year_prev_vat_cents: 760000,
  },
  mail_days: [
    { day: "2026-09-27", label: "Aujourd’hui", sent: 1, failed: 0, failed_numbers: [] },
    { day: "2026-09-26", label: "Hier", sent: 4, failed: 1, failed_numbers: ["F2026-0018"] },
    { day: "2026-09-25", label: "25/09/2026", sent: 0, failed: 0, failed_numbers: [] },
    { day: "2026-09-24", label: "24/09/2026", sent: 2, failed: 0, failed_numbers: [] },
  ],
  activity: [
    { id: "a1", kind: "payment", title: "Paiement 1 920 € · Studio Seth", meta: "Aujourd’hui · F2026-0014" },
    { id: "a2", kind: "invoice", title: "Facture F2026-0020 validée", meta: "Hier · Atelier Nord · 1 000 €" },
    { id: "a3", kind: "payment", title: "Paiement 480 € · Café des Halles", meta: "12 sept. · F2026-0011" },
    { id: "a4", kind: "credit", title: "Avoir A2026-0002", meta: "11 sept. · Maison Lumen · −120 €" },
    { id: "a5", kind: "invoice", title: "Facture F2026-0019 validée", meta: "10 sept. · Café des Halles · 1 200 €" },
  ],
};

/** Encaissé HT fictif, stable selon la durée (période actuelle un peu au-dessus). */
export function demoEncaissementHt(from: string, to: string, previous: boolean): number {
  return inclusiveDays(from, to) * (previous ? 240 : 288);
}
